import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiRequest } from "../../api/http";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchEmployeeDetail } from "../../store/slices/employeesSlice";
import { EmployeeContractResponse, EmployeeContract } from "../../types/admin";
import { formatDate, formatTemplateVersion } from "../../utils/admin";

const EmployeeContractsPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError } = useAppSelector((state) => state.employees);

  const [contracts, setContracts] = useState<EmployeeContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employeeId && (!detail || detail.employee_id !== Number(employeeId))) {
      dispatch(fetchEmployeeDetail(Number(employeeId)));
    }
  }, [dispatch, employeeId, detail]);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!detail?.branch_id || !employeeId) return;
      
      setLoading(true);
      setError(null);
      try {
        const res = await apiRequest<EmployeeContractResponse>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/contracts`);
        setContracts(res.items);
      } catch (err: any) {
        setError(err.message || "근로계약서 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    if (detail) {
      fetchDocs();
    }
  }, [detail, employeeId]);

  return (
    <section className="admin-page">
      <header className="detail-header">
        <button className="back-btn" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h2 className="section-title">
          계약서 목록 {detail ? `(${detail.employee_name})` : ''}
        </h2>
      </header>

      {loadingDetail && <p className="loading-text">직원 정보를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}

      {!loadingDetail && !detailError && (
        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '16px', marginTop: '24px' }}>
          {loading && <p>데이터를 불러오는 중입니다...</p>}
          {error && <p className="error-text">{error}</p>}
          
          {!loading && !error && (
            <div className="table-shell" style={{ overflowX: 'auto' }}>
              <table className="admin-table compact" style={{ whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>계약서명</th>
                    <th>유형</th>
                    <th>작성일자</th>
                    <th>상태</th>
                    <th style={{ textAlign: 'center' }}>상세</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map(contract => (
                    <tr key={contract.contract_id}>
                      <td>{contract.title}</td>
                      <td>{formatTemplateVersion(contract.template_version)}</td>
                      <td>{formatDate(contract.created_at)}</td>
                      <td>{contract.status === 'completed' ? '완료' : contract.status === 'draft' ? '진행중' : contract.status}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="detail-icon-btn"
                          style={{ 
                            padding: '4px 8px', 
                            fontSize: '12px', 
                            whiteSpace: 'nowrap', 
                            display: 'inline-block',
                            color: '#1d1d1f',
                            border: '1px solid #e7e8ef',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                          onClick={() => navigate(`/admin/employees/${employeeId}/contracts/${contract.contract_id}`)}
                        >
                          상세보기
                        </button>
                      </td>
                    </tr>
                  )                  )}
                  {contracts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty-row">등록된 계약서가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EmployeeContractsPage;
